import React, { useState, useEffect } from "react";
import moment from "moment";
import { ProgramacionMantenimientoService } from "services/ProgramacionMantenimientoService";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

const ESTADO_CONFIG = {
  activo: { label: "Activo", className: "bg-primary" },
  pausado: { label: "Pausado", className: "bg-warning text-dark" },
  cancelado: { label: "Cancelado", className: "bg-danger" },
};

const TIPO_LABELS = {
  preventivo: "Preventivo",
  correctivo: "Correctivo",
};

const FRECUENCIA_LABELS = {
  unico: "Único",
  mensual: "Mensual",
  trimestral: "Trimestral",
  anual: "Anual",
};

const formatDate = (date) => (!date ? "-" : moment(date).format("DD MMM YYYY"));

const CardSection = ({ icon, title, children }) => (
  <div className="card border-light mb-3">
    <div className="card-header bg-transparent border-bottom py-2">
      <i className={`fas ${icon} text-primary me-2`} />
      <strong className="text-uppercase small text-muted">{title}</strong>
    </div>
    <div className="card-body py-2">{children}</div>
  </div>
);

const FieldRow = ({ label, value, fullWidth }) => (
  <div
    className={`d-flex justify-content-between py-1 ${fullWidth ? "flex-column" : ""}`}
  >
    <span className="text-muted">{label}</span>
    <span className={`fw-medium ${fullWidth ? "mt-1" : ""}`}>
      {value || "-"}
    </span>
  </div>
);

const ProgramacionDetailsModal = ({ show, onHide, uuid }) => {
  const [programacion, setProgramacion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !uuid) return;

    setLoading(true);
    ProgramacionMantenimientoService.obtener(uuid)
      .then((res) => setProgramacion(res.data))
      .catch(() => setProgramacion(null))
      .finally(() => setLoading(false));
  }, [show, uuid]);

  useEffect(() => {
    if (!show) setProgramacion(null);
  }, [show]);

  if (!show) return null;

  const estadoConfig = ESTADO_CONFIG[programacion?.estado] || {
    label: programacion?.estado,
    className: "bg-secondary",
  };

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1060 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-calendar-check text-primary me-2" />
                Detalles de la Programación
              </h5>
              <button type="button" className="btn-close" onClick={onHide} />
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="text-center py-4">
                  <SpinnerCitoPlus text="Cargando detalles..." />
                </div>
              ) : !programacion ? (
                <div className="alert alert-danger mb-0">
                  No fue posible cargar los detalles.
                </div>
              ) : (
                <>
                  <CardSection
                    icon="fa-info-circle"
                    title="Información General"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Estado</span>
                      <span className={`badge ${estadoConfig.className}`}>
                        {estadoConfig.label}
                      </span>
                    </div>
                    <FieldRow label="Título" value={programacion.titulo} />
                    <FieldRow
                      label="Descripción"
                      value={programacion.descripcion}
                      fullWidth
                    />
                  </CardSection>

                  <CardSection icon="fa-box" title="Activo">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-cube text-secondary me-2" />
                      <div>
                        <div className="fw-medium">
                          {programacion.activo?.nombre || "-"}
                        </div>
                        {programacion.activo?.consecutivo && (
                          <small className="text-muted">
                            {programacion.activo.consecutivo}
                          </small>
                        )}
                      </div>
                    </div>
                  </CardSection>

                  <CardSection icon="fa-calendar-alt" title="Programación">
                    <div className="row">
                      <div className="col-4">
                        <div className="text-muted small">Tipo</div>
                        <div className="fw-medium">
                          {TIPO_LABELS[programacion.tipo] ||
                            programacion.tipo ||
                            "-"}
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted small">Frecuencia</div>
                        <div className="fw-medium">
                          {FRECUENCIA_LABELS[programacion.frecuencia] ||
                            programacion.frecuencia ||
                            "-"}
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted small">
                          Días de anticipación
                        </div>
                        <div className="fw-medium">
                          {programacion.dias_anticipacion ?? "-"}
                        </div>
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="row">
                      <div className="col-4">
                        <div className="text-muted small">Fecha inicio</div>
                        <div className="fw-medium">
                          {formatDate(programacion.fecha_inicio)}
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted small">
                          Próxima ejecución
                        </div>
                        <div className="fw-medium">
                          {formatDate(programacion.fecha_siguiente)}
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted small">Última ejecución</div>
                        <div className="fw-medium">
                          {formatDate(programacion.ultima_ejecucion)}
                        </div>
                      </div>
                    </div>
                  </CardSection>

                  <CardSection icon="fa-user" title="Responsable">
                    <div className="row">
                      <div className="col-6">
                        <div className="text-muted small">Tipo</div>
                        <div className="fw-medium text-capitalize">
                          {programacion.responsable_tipo || "-"}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-muted small">Proveedor</div>
                        <div className="fw-medium">
                          {programacion.proveedor_nombre || "-"}
                        </div>
                      </div>
                    </div>
                  </CardSection>

                  <CardSection icon="fa-bell" title="Notificaciones">
                    <div className="row">
                      <div className="col-6">
                        <div className="text-muted small">Email</div>
                        <div className="fw-medium">
                          {programacion.notificacion_email ? (
                            <span className="badge bg-success">Activado</span>
                          ) : (
                            <span className="badge bg-secondary">
                              Desactivado
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-muted small">WhatsApp</div>
                        <div className="fw-medium">
                          {programacion.notificacion_whatsapp ? (
                            <span className="badge bg-success">Activado</span>
                          ) : (
                            <span className="badge bg-secondary">
                              Desactivado
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardSection>

                  <CardSection icon="fa-clock" title="Registro">
                    <div className="text-muted small">Fecha de creación</div>
                    <div className="fw-medium">{programacion.created_at}</div>
                  </CardSection>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onHide}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onHide}
      />
    </>
  );
};

export default ProgramacionDetailsModal;
